'use client';

import { Play, ArrowCounterClockwise, MagnifyingGlass, X, SlidersHorizontal, LockSimple } from '@phosphor-icons/react';
import { useState, useRef, useMemo, useEffect } from 'react';
import { WebinarRegistrationForm } from '@/components/seasons/WebinarRegistrationForm';

export interface TranscriptEntry {
  time: string;
  text: string;
}

const FIGMA_GRADIENTS: Record<string, string> = {
  'winter-night':  'linear-gradient(0deg,#000a1b 0%,#053bee 100%)',
  'night-sky':     'linear-gradient(0deg,#000a1b 0%,#a0cce0 100%)',
  'ember-night':   'linear-gradient(90deg,#000a1b 0%,#eb5324 100%)',
  'dusk-glow':     'linear-gradient(0deg,#000a1b 0%,#ffcc9c 100%)',
  'deep-forest':   'linear-gradient(0deg,#000a1b 0%,#05604f 100%)',
  'moonlit-grove': 'linear-gradient(0deg,#000a1b 0%,#aad097 100%)',
  'night-bloom':   'linear-gradient(0deg,#000a1b 0%,#58364e 100%)',
  'late-frost':    'linear-gradient(0deg,#000a1b 0%,#ffadd9 100%)',
  'golden-hour':   'linear-gradient(0deg,#000a1b 0%,#ffbf00 100%)',
  'lantern-light': 'linear-gradient(0deg,#000a1b 0%,#ffe08a 100%)',
  'horizon':       'linear-gradient(0deg,#000a1b 0%,#f0f0f0 100%)',
  'open-sky':      'linear-gradient(0deg,#053bee 0%,#a0cce0 100%)',
  'summer-storm':  'linear-gradient(0deg,#053bee 0%,#eb5324 100%)',
  'dawn-break':    'linear-gradient(0deg,#053bee 0%,#ffcc9c 100%)',
  'deep-current':  'linear-gradient(0deg,#053bee 0%,#05604f 100%)',
  'spring-rain':   'linear-gradient(0deg,#053bee 0%,#aad097 100%)',
  'indigo-dusk':   'linear-gradient(0deg,#053bee 0%,#58364e 100%)',
  'april-shower':  'linear-gradient(0deg,#053bee 0%,#ffadd9 100%)',
  'equinox':       'linear-gradient(0deg,#053bee 0%,#ffbf00 100%)',
  'bright-spark':  'linear-gradient(0deg,#053bee 0%,#ffe08a 100%)',
  'first-frost':   'linear-gradient(0deg,#053bee 0%,#f0f0f0 100%)',
  'coastal':       'linear-gradient(0deg,#a0cce0 0%,#05604f 100%)',
  'fresh-air':     'linear-gradient(180deg,#a0cce0 0%,#aad097 100%)',
  'cloud-wash':    'linear-gradient(0deg,#a0cce0 0%,#f0f0f0 100%)',
  'warm-ember':    'linear-gradient(0deg,#eb5324 0%,#ffcc9c 100%)',
  'autumn-field':  'linear-gradient(0deg,#eb5324 0%,#aad097 100%)',
  'morning-dew':   'linear-gradient(90deg,#aad097 0%,#ffe08a 100%)',
  'vanilla':       'linear-gradient(0deg,#ffcc9c 0%,#f0f0f0 100%)',
};

interface SpeakerItem {
  name: string;
  role?: string;
  photo?: string;
}

interface Props {
  slug: string;
  webinarTitle: string;
  videoUrl?: string;
  heroImg?: string;
  speakers?: SpeakerItem[];
  originallyAired?: string;
  durationMinutes?: number;
  transcript?: TranscriptEntry[];
  bannerGradient?: string;
  /** Read server-side in page.tsx from the request headers — see getClientIp. */
  visitorIp?: string;
}

function extractYouTubeId(input: string): string | null {
  const m = input.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([\w-]+)/,
  );
  return m ? m[1] : null;
}


function extractVimeoId(input: string): string | null {
  if (/^\d+$/.test(input)) return input;
  // Direct vimeo.com or player.vimeo.com URL
  const direct = input.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (direct) return direct[1];
  // Embedly-wrapped Vimeo — extract ID from URL-encoded src param
  const embedlyMatch = input.match(/[?&]src=([^&]+)/);
  if (embedlyMatch) {
    const decoded = decodeURIComponent(embedlyMatch[1]);
    const fromSrc = decoded.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (fromSrc) return fromSrc[1];
  }
  return null;
}

function buildPreviewUrl(input: string | undefined): string | null {
  if (!input) return null;
  const ytId = extractYouTubeId(input);
  if (ytId) {
    // Locked preview — no controls, no keyboard, no fullscreen, no scrubbing,
    // no related videos, no annotations, no YouTube branding (modestbranding).
    // A click-blocker overlay sits over the iframe to prevent ANY interaction.
    const params = new URLSearchParams({
      autoplay: '1',
      start: '0',
      end: '20',
      controls: '0',
      disablekb: '1',
      fs: '0',
      iv_load_policy: '3',
      modestbranding: '1',
      playsinline: '1',
      rel: '0',
      enablejsapi: '1',
    });
    return `https://www.youtube.com/embed/${ytId}?${params.toString()}`;
  }
  const vimeoId = extractVimeoId(input);
  if (vimeoId) {
    // Locked 20s preview — no controls, no keyboard, click-blocker overlay handles the rest
    const params = new URLSearchParams({
      autoplay: '1',
      controls: '0',
      keyboard: '0',
      pip: '0',
      playsinline: '1',
      title: '0',
      byline: '0',
      portrait: '0',
      dnt: '1',
      api: '1',
    });
    return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}#t=0s`;
  }
  return null;
}

function buildFullUrl(input: string | undefined): string | null {
  if (!input) return null;
  const ytId = extractYouTubeId(input);
  if (ytId) {
    return `https://www.youtube.com/embed/${ytId}?rel=0&enablejsapi=1`;
  }
  const vimeoId = extractVimeoId(input);
  if (vimeoId) {
    const params = new URLSearchParams({
      title: '0',
      byline: '0',
      portrait: '0',
      dnt: '1',
      api: '1',
      player_id: 'wbn-player',
    });
    return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
  }
  return null;
}

function parseTimeToSeconds(time: string): number {
  const parts = time.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] ?? 0;
}

// ── Transcript panel ──────────────────────────────────────────────────────────
function TranscriptPanel({
  entries,
  onSeek,
  onClose,
}: {
  entries: TranscriptEntry[];
  onSeek: (seconds: number) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e => e.text.toLowerCase().includes(q) || e.time.includes(q));
  }, [entries, query]);

  return (
    <div className="wbn-transcript">
      {/* Header */}
      <div className="wbn-transcript__hd">
        <span className="wbn-transcript__title">Transcript</span>
        <div className="wbn-transcript__hd-actions">
          <button
            type="button"
            className="wbn-transcript__icon-btn"
            aria-label="Close transcript"
            onClick={onClose}
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="wbn-transcript__search-wrap">
        <MagnifyingGlass size={14} className="wbn-transcript__search-icon" aria-hidden="true" />
        <input
          className="wbn-transcript__search"
          type="search"
          placeholder="Search transcript"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {/* Entries */}
      <div className="wbn-transcript__body">
        {filtered.length === 0 ? (
          <p className="wbn-transcript__empty">No results for "{query}"</p>
        ) : (
          filtered.map((entry, i) => (
            <button
              key={i}
              type="button"
              className="wbn-transcript__entry"
              onClick={() => onSeek(parseTimeToSeconds(entry.time))}
            >
              <span className="wbn-transcript__entry-text">{entry.text}</span>
              <span className="wbn-transcript__entry-time">{entry.time}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function WebinarVideoPlayer({ slug, webinarTitle, videoUrl, heroImg, speakers, transcript, bannerGradient, visitorIp }: Props) {
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewEnded, setPreviewEnded] = useState(false);
  const [remainingSec, setRemainingSec] = useState(20);
  const [unlocked, setUnlocked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);


  const previewSrc = buildPreviewUrl(videoUrl);
  const fullSrc = buildFullUrl(videoUrl);
  const hasTranscript = Array.isArray(transcript) && transcript.length > 0;

  const gateStyle = bannerGradient && FIGMA_GRADIENTS[bannerGradient]
    ? { '--wbn-gradient': FIGMA_GRADIENTS[bannerGradient] } as React.CSSProperties
    : undefined;

  // ── Auto-open Vimeo transcript panel after player is ready ─────────────────
  useEffect(() => {
    if (!playing || !fullSrc?.includes('vimeo.com')) return;
    function handleMessage(event: MessageEvent) {
      try {
        if (!String(event.origin).includes('vimeo.com')) return;
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.event === 'ready') {
          setTimeout(() => {
            playerRef.current?.contentWindow?.postMessage(
              JSON.stringify({ method: 'setTranscriptVisibility', value: true }),
              'https://player.vimeo.com',
            );
          }, 800);
        }
      } catch { /* ignore parse errors */ }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [playing, fullSrc]);

  // ── Preview hard-stop timer ─────────────────────────────────────────────────
  // When the 20s preview is playing, run a JS countdown. At zero, pause the
  // iframe via postMessage (belt-and-suspenders alongside YouTube's end=20)
  // and show the "Register to continue" overlay. Click-blocker overlay
  // already prevents the user from interacting with the iframe directly.
  useEffect(() => {
    if (!previewPlaying || previewEnded) return;
    setRemainingSec(20);
    const tickInterval = setInterval(() => {
      setRemainingSec(s => (s > 0 ? s - 1 : 0));
    }, 1000);
    const endTimer = setTimeout(() => {
      // Force-pause both YouTube and Vimeo (whichever is active)
      previewRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*',
      );
      previewRef.current?.contentWindow?.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*',
      );
      setPreviewEnded(true);
    }, 20_000);
    return () => {
      clearInterval(tickInterval);
      clearTimeout(endTimer);
    };
  }, [previewPlaying, previewEnded]);

  function startPreview() {
    setPreviewEnded(false);
    setPreviewPlaying(true);
  }

  function seekTo(seconds: number) {
    // YouTube postMessage seek
    playerRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'seekTo', args: [seconds, true] }),
      'https://www.youtube-nocookie.com',
    );
    // Vimeo postMessage seek
    playerRef.current?.contentWindow?.postMessage(
      JSON.stringify({ method: 'setCurrentTime', value: seconds }),
      'https://player.vimeo.com',
    );
  }

  // ── Unlocked: full player ─────────────────────────────────────────────────────
  if (unlocked) {
    if (fullSrc) {
      return (
        <div className={`wbn-player-full${hasTranscript && transcriptOpen ? ' wbn-player-full--with-transcript' : ''}`}>
          {/* Video */}
          <div className="wbn-player-full__video-wrap">
            <iframe
              ref={playerRef}
              src={fullSrc}
              title={webinarTitle}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; gyroscope; accelerometer"
              allowFullScreen
              className="wbn-player-full__iframe"
            />
            {/* Transcript toggle button — visible when transcript is closed */}
            {hasTranscript && !transcriptOpen && (
              <button
                type="button"
                className="wbn-player-full__transcript-toggle"
                onClick={() => setTranscriptOpen(true)}
                aria-label="Open transcript"
              >
                <SlidersHorizontal size={14} weight="bold" />
                Transcript
              </button>
            )}
          </div>

          {/* Transcript panel */}
          {hasTranscript && transcriptOpen && (
            <TranscriptPanel
              entries={transcript!}
              onSeek={seekTo}
              onClose={() => setTranscriptOpen(false)}
            />
          )}
        </div>
      );
    }

    // No video yet
    return (
      <div className="wbn-gate wbn-gate--video" style={gateStyle}>
        <div className="wbn-gate__no-video">
          <Play size={56} weight="fill" style={{ color: 'var(--seasons-color-on-dark-heading)' }} />
          <p className="type-body-md" style={{ color: 'var(--seasons-color-on-dark-body)', marginTop: 'var(--seasons-space-3)' }}>
            Recording coming soon.
          </p>
        </div>
      </div>
    );
  }

  // ── Gate: thumbnail/preview + registration form ──────────────────────────────
  return (
    <div className="wbn-gate" style={gateStyle}>
      {/* Left — thumbnail / preview player */}
      <div className="wbn-gate__left">
        <div className="wbn-gate__thumb">
          {previewPlaying && previewSrc ? (
            <>
              {/* Locked preview iframe — no controls, no scrubbing */}
              <iframe
                ref={previewRef}
                className="wbn-gate__preview-iframe"
                src={previewSrc}
                title={`${webinarTitle} — 20s preview`}
                allow="autoplay"
                /* no allowFullScreen — fullscreen would expose controls */
              />
              {/* Click-blocker — covers the iframe so YouTube/Vimeo can't be clicked,
                  scrubbed, fullscreened, or right-clicked. Pointer events captured here. */}
              <div className="wbn-gate__preview-blocker" aria-hidden="true" onContextMenu={(e) => e.preventDefault()} />

              {!previewEnded ? (
                /* Countdown pill while preview plays */
                <div className="wbn-gate__preview-countdown" aria-live="polite">
                  <LockSimple size={14} weight="fill" />
                  Preview · {remainingSec}s
                </div>
              ) : (
                /* After 20s — register-to-continue overlay */
                <div className="wbn-gate__preview-ended">
                  <div className="wbn-gate__preview-ended-body">
                    <LockSimple size={28} weight="fill" />
                    <p className="wbn-gate__preview-ended-heading">Preview ended</p>
                    <p className="wbn-gate__preview-ended-sub">Register on the right to watch the full recording.</p>
                    <button
                      type="button"
                      className="wbn-gate__preview-ended-replay"
                      onClick={() => { setPreviewPlaying(false); setPreviewEnded(false); }}
                    >
                      <ArrowCounterClockwise size={16} weight="bold" />
                      Replay preview
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {heroImg ? (
                <img src={heroImg} alt="" className="wbn-gate__thumb-img" />
              ) : (
                <div className="wbn-gate__thumb-fallback" aria-hidden="true" />
              )}
              <button
                type="button"
                className="wbn-gate__play-overlay"
                aria-label={previewSrc ? 'Watch 20-second preview' : 'Register to watch'}
                onClick={() => { if (previewSrc) startPreview(); }}
                style={!previewSrc ? { cursor: 'default', pointerEvents: 'none' } : undefined}
              >
                <span className="wbn-gate__play-btn-circle">
                  <Play size={32} weight="fill" />
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Right — registration form */}
      <div className="wbn-gate__right">
        <div className="wbn-gate__form-card">
          <div className="wbn-gate__form-hd">
            <h2 className="wbn-gate__form-heading">Register to view</h2>
            <p className="wbn-gate__form-sub">Fill in the form below to watch the video.</p>
          </div>
          <WebinarRegistrationForm
            slug={slug}
            webinarTitle={webinarTitle}
            compact
            hideHeading
            ctaLabel="Watch now"
            visitorIp={visitorIp}
            onSubmitted={() => {
              setUnlocked(true);
            }}
          />
        </div>
      </div>
    </div>
  );
}
